import { useSnackbar } from 'notistack';
import {
	CreditOutputLineItem,
	OutputAnalysisGroup,
	OutputAnalysisStatus,
	OutputAnalysisStatusMap,
	OutputAnalysisType,
	SummaryOutputStatus,
} from '../shared/models';
import { useApi } from '../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { EnumUtils, FormikUtils } from '../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { getApiResponseData } from '../shared/utils/axios';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { QueryKeys } from '../shared/hooks';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../shared/components';
import { EmptyGuid } from '../shared/constants';
import { Grid } from '@mui/material';

interface ItemFormProps {
	item: CreditOutputLineItem | null;
	close: () => void;
}

export const OutputAnalysisItemForm: React.FC<ItemFormProps> = ({ item, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();

	const itemMutation = useMutation(
		async ({ values }: ItemFormMutationVariables) => {
			const { data, error } = await getApiResponseData<CreditOutputLineItem>(
				api.post(CerebroApiEndpoints.Matching.OutputAnalysisItem(guid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Matching.OutputAnalysis(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				snackbar.enqueueSnackbar('Updated output analysis.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={item != null} onClose={close}>
			<CerebroHeading>Update Output Analysis Item</CerebroHeading>
			<CerebroHeading size="h3" component="h3">
				{EnumUtils.GetOutputAnalysisGroupDescription(item?.groupType)} -{' '}
				{EnumUtils.GetOutputAnalysisTypeDescription(item?.outputType)}
			</CerebroHeading>

			<Formik
				initialValues={{
					id: item?.id ?? 0,
					guid: item?.guid ?? EmptyGuid,
					displayIndex: item?.displayIndex ?? 0,
					groupType: item?.groupType ?? 'None',
					outputType: item?.outputType ?? 'TtmRevenue',
					outputStatus: item?.outputStatus ?? 'MissingData',
					summaryStatus: item?.summaryStatus ?? 'None',
					comments: item?.comments ?? '',
					isVisibleToBorrower: item?.isVisibleToBorrower ?? true,
					isDoneEditing: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(itemMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="outputStatus">Rating</CerebroLabel>
									<Field
										id="outputStatus"
										as={CerebroDropdown}
										name="outputStatus"
										disabled={isSubmitting}
									>
										{Object.entries(OutputAnalysisStatusMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
									{errors.outputStatus && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.outputStatus}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="comments">Comments</CerebroLabel>
									<Field
										id="comments"
										as={CerebroTextarea}
										name="comments"
										rows={6}
										disabled={isSubmitting}
										error={touched.comments && errors.comments !== undefined}
									/>
									{errors.comments && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.comments}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="isVisibleToBorrower">Visible to Borrower</CerebroLabel>
									<Field
										id="isVisibleToBorrower"
										as={CerebroYesNo}
										name="isVisibleToBorrower"
										disabled={isSubmitting}
										error={touched.isVisibleToBorrower && errors.isVisibleToBorrower !== undefined}
									/>
									{errors.isVisibleToBorrower && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.isVisibleToBorrower}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="isDoneEditing">Done Editing Credit Analysis?</CerebroLabel>
									<Field
										id="isDoneEditing"
										as={CerebroYesNo}
										name="isDoneEditing"
										disabled={isSubmitting}
									/>
									<CerebroText variant="hint">
										Set to Yes if this is the last change you are making and the deal setting will
										be updated to turn off edit mode for this page.
									</CerebroText>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type ItemFormMutationVariables = FormikUtils.MutateOnSubmitVariables<ItemFormValues>;
interface ItemFormValues {
	id: number;
	guid: string;
	displayIndex: number;
	groupType: OutputAnalysisGroup;
	outputType: OutputAnalysisType;
	outputStatus: OutputAnalysisStatus;
	summaryStatus: SummaryOutputStatus;
	comments: string;
	isVisibleToBorrower: boolean;
	isDoneEditing: boolean;
}
type ItemFormValueErrors = FormikErrors<ItemFormValues>;

function validateForm(values: ItemFormValues) {
	const errors: ItemFormValueErrors = {};

	return errors;
}
